import React, { useState, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import ListingGrid from "@/components/listings/ListingGrid";
import { Loader2, Search, X, ChevronLeft, ChevronRight, Calendar, Package } from "lucide-react";
import FollowSellerButton from "@/components/common/FollowSellerButton";
import VerifiedBadge from "@/components/common/VerifiedBadge";
import NearestChip from "@/components/listings/NearestChip";

const PAGE_SIZE = 20;

const SORT_OPTIONS = [
  // Iter 784 — "recent" = featured rotation; "latest" = strictly newest.
  { value: "recent", label: "Featured" },
  { value: "latest", label: "Latest" },
  { value: "price_asc", label: "Price ↑" },
  { value: "price_desc", label: "Price ↓" },
];

function memberSinceLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-NZ", { month: "long", year: "numeric" });
}

export default function SellerPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();

  // URL is source of truth for search, category filter, sort, page
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const sort = params.get("sort") || "recent";
  // Iter-600 §1 — nearest-first on the seller page too.
  const lat = params.get("lat") || "";
  const lng = params.get("lng") || "";
  const nearestActive = sort === "distance" && !!lat && !!lng;
  const page = Math.max(1, parseInt(params.get("page") || "1", 10));

  // Local input mirrors ?q for typing UX
  const [qInput, setQInput] = useState(q);
  React.useEffect(() => { setQInput(q); }, [q]);

  const updateParams = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k);
      else next.set(k, String(v));
    });
    // Reset page when any filter changes (unless we're setting page itself)
    if (!("page" in patch)) next.delete("page");
    setParams(next, { replace: false });
  };

  const sellerQ = useQuery({
    queryKey: ["seller", id],
    queryFn: async () => (await sharedApi.get(`/classifieds/sellers/${id}`)).data,
    enabled: !!id,
    staleTime: 60_000,
  });

  const listingsQ = useQuery({
    queryKey: ["seller-listings", id, q, category, sort, page, lat, lng],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/listings", {
        params: {
          user_id: id,
          q: q || undefined,
          category: category || undefined,
          sort,
          lat: nearestActive ? lat : undefined,
          lng: nearestActive ? lng : undefined,
          page,
          limit: PAGE_SIZE,
        },
      });
      return data;
    },
    enabled: !!id,
    keepPreviousData: true,
  });

  const listings = listingsQ.data?.listings || [];
  const total = listingsQ.data?.total ?? 0;
  const hasMore = listingsQ.data?.has_more ?? false;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const submitSearch = (e) => {
    e.preventDefault();
    updateParams({ q: qInput.trim() });
  };

  const clearSearch = () => {
    setQInput("");
    updateParams({ q: "" });
  };

  const seller = sellerQ.data;
  const initial = (seller?.name || "S").trim().charAt(0).toUpperCase();

  // Iter-370 — never render blank on fetch failure: full-page error + retry
  // when the seller profile itself can't load.
  if (sellerQ.isError) {
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <Seo title="Seller" description="" />
        <Breadcrumbs items={[{ label: "Sellers" }, { label: "…" }]} />
        <div className="mt-6 rounded-2xl bg-white shadow-card p-10 text-center" data-testid="seller-error">
          <Package className="w-10 h-10 mx-auto text-slate-300" />
          <h1 className="mt-4 font-display text-xl font-bold text-slate-900">Couldn&apos;t load this seller</h1>
          <p className="mt-1 text-sm text-slate-500">Check your connection and try again.</p>
          <button
            type="button"
            onClick={() => sellerQ.refetch()}
            data-testid="seller-error-retry"
            className="mt-4 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm px-5 py-2 transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
      <Seo title={seller?.name ? `${seller.name} on Allsale` : "Seller"} description={seller?.name ? `All listings by ${seller.name} on Allsale Classified.` : ""} />
      <Breadcrumbs items={[{ label: "Sellers" }, { label: seller?.name || "…" }]} />

      {/* Seller profile card */}
      <div className="mt-6 rounded-3xl bg-white shadow-card border border-soft overflow-hidden" data-testid="seller-profile-card">
        <div className="p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5">
          {seller?.photo ? (
            <img
              src={seller.photo}
              alt={seller.name || "Seller"}
              data-testid="seller-photo"
              className="w-24 h-24 rounded-full object-cover ring-4 ring-white shadow-md"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div
              data-testid="seller-avatar-fallback"
              className="w-24 h-24 rounded-full ring-4 ring-white shadow-md grid place-items-center bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] text-3xl font-black"
            >
              {initial}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0 flex items-center gap-1.5 flex-wrap">
                <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 inline-flex items-center gap-1.5" data-testid="seller-name">
                  <span>{sellerQ.isLoading ? "Loading…" : seller?.name || "Seller"}</span>
                  <VerifiedBadge verified={seller?.verified} className="text-lg" />
                </h1>
                {seller?.is_business && (
                  <span
                    data-testid="seller-business-chip"
                    title="Verified business seller"
                    className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 text-[10px] font-bold tracking-widest"
                  >
                    🏢 Business
                  </span>
                )}
              </div>
              {seller?.id && <FollowSellerButton sellerId={seller.id} sellerName={seller.name} />}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
              {seller?.member_since && (
                <span className="inline-flex items-center gap-1.5" data-testid="seller-member-since">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  Member since {memberSinceLabel(seller.member_since)}
                </span>
              )}
              {typeof seller?.total_active === "number" && (
                <span className="inline-flex items-center gap-1.5" data-testid="seller-total-active">
                  <Package className="w-3.5 h-3.5 text-slate-400" />
                  {seller.total_active} active {seller.total_active === 1 ? "ad" : "ads"}
                </span>
              )}
            </div>

            {/* Category chips (what they sell) */}
            {Array.isArray(seller?.categories) && seller.categories.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="seller-category-chips">
                <button
                  type="button"
                  onClick={() => updateParams({ category: "" })}
                  data-testid="seller-cat-all"
                  className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold ring-1 transition-colors ${
                    !category
                      ? "bg-[color:var(--brand-blue)] text-white ring-transparent"
                      : "bg-white text-slate-700 ring-slate-200 hover:ring-[color:var(--brand-blue)]"
                  }`}
                >
                  All <span className="tabular-nums opacity-70">{seller.total_active ?? ""}</span>
                </button>
                {seller.categories.map((c) => (
                  <button
                    key={c.slug}
                    type="button"
                    onClick={() => updateParams({ category: c.slug })}
                    data-testid={`seller-cat-${c.slug}`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold ring-1 transition-colors ${
                      category === c.slug
                        ? "bg-[color:var(--brand-blue)] text-white ring-transparent"
                        : "bg-white text-slate-700 ring-slate-200 hover:ring-[color:var(--brand-blue)]"
                    }`}
                  >
                    {c.name} <span className="tabular-nums opacity-70">{c.count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search + sort */}
      <div className="mt-6 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <form onSubmit={submitSearch} className="flex-1 relative" data-testid="seller-search-form">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            data-testid="seller-search-input"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder={`Search ${seller?.name ? `${seller.name}'s` : "seller's"} ads`}
            className="w-full pl-10 pr-10 py-2.5 rounded-full bg-white border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
          />
          {qInput && (
            <button
              type="button"
              onClick={clearSearch}
              data-testid="seller-search-clear"
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-slate-400 hover:text-slate-700"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </form>

        <div className="inline-flex bg-white rounded-full p-1 border border-soft shrink-0" data-testid="seller-sort">
          {SORT_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => updateParams({ sort: o.value, lat: "", lng: "" })}
              data-testid={`seller-sort-${o.value}`}
              className={`px-3.5 py-1.5 text-xs font-bold rounded-full transition-colors ${
                sort === o.value ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        {/* Iter-600 §1 — nearest-first chip (location asked on tap only) */}
        <NearestChip
          active={nearestActive}
          onCoords={({ lat: la, lng: lo }) => updateParams({ sort: "distance", lat: la, lng: lo })}
          onClear={() => updateParams({ sort: "recent", lat: "", lng: "" })}
          testId="seller-nearest-chip"
        />
      </div>

      {/* Results summary */}
      <div className="mt-4 mb-3 flex items-center justify-between text-sm text-slate-600" data-testid="seller-results-summary">
        {listingsQ.isLoading ? (
          <span>Loading listings…</span>
        ) : (
          <span>
            {total.toLocaleString("en-NZ")} {total === 1 ? "listing" : "listings"}
            {q && <> for “<span className="font-semibold text-slate-800">{q}</span>”</>}
            {category && <> in <span className="font-semibold text-slate-800">{seller?.categories?.find((c) => c.slug === category)?.name || category}</span></>}
          </span>
        )}
      </div>

      {/* Listings grid */}
      {listingsQ.isLoading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
      ) : listingsQ.isError ? (
        /* Iter-370 — listings fetch failure gets an explicit error + retry */
        <div className="rounded-2xl bg-white shadow-card p-10 text-center" data-testid="seller-listings-error">
          <Package className="w-10 h-10 mx-auto text-slate-300" />
          <h2 className="mt-4 font-display text-xl font-bold text-slate-900">Couldn&apos;t load the listings</h2>
          <p className="mt-1 text-sm text-slate-500">Check your connection and try again.</p>
          <button
            type="button"
            onClick={() => listingsQ.refetch()}
            data-testid="seller-listings-retry"
            className="mt-4 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm px-5 py-2 transition-colors"
          >
            Try again
          </button>
        </div>
      ) : listings.length === 0 ? (
        <div className="rounded-2xl bg-white shadow-card p-10 text-center" data-testid="seller-empty">
          <Package className="w-10 h-10 mx-auto text-slate-300" />
          <h2 className="mt-4 font-display text-xl font-bold text-slate-900">Nothing matches</h2>
          <p className="mt-1 text-sm text-slate-500">Try clearing the search or picking a different category.</p>
          {(q || category) && (
            <button
              type="button"
              onClick={() => setParams(new URLSearchParams(), { replace: true })}
              data-testid="seller-empty-reset"
              className="mt-4 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm px-4 py-2 transition-colors"
            >
              Reset filters
            </button>
          )}
        </div>
      ) : (
        <>
          <ListingGrid listings={listings} />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-2" data-testid="seller-pagination">
              <button
                type="button"
                onClick={() => updateParams({ page: page - 1 })}
                disabled={page <= 1}
                data-testid="seller-page-prev"
                className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-semibold bg-white border border-soft disabled:opacity-40 hover:border-[color:var(--brand-blue)] transition-colors"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <span className="text-sm text-slate-600 tabular-nums px-3" data-testid="seller-page-label">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => updateParams({ page: page + 1 })}
                disabled={!hasMore}
                data-testid="seller-page-next"
                className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-semibold bg-white border border-soft disabled:opacity-40 hover:border-[color:var(--brand-blue)] transition-colors"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
